import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {HYDRATE} from 'next-redux-wrapper';

interface AppLayoutState {
    questionBoard: boolean;
    codeEditorBoard: boolean;
    terminalBoard: boolean;
    whiteboard: boolean;
    enableCodeEditor: boolean;
    enableTerminal: boolean;
    enableWhiteboard: boolean;
    enableQuestionBoard: boolean;
    enableVideo: boolean;
    nextVideoRequested: boolean;
    agentIsListening: boolean;
    agentIsSpeaking: boolean;
    helpBoard: boolean;
}

const initialState: AppLayoutState = {
    questionBoard: false,
    codeEditorBoard: false,
    terminalBoard: false,
    whiteboard: false,
    helpBoard: false,
    enableCodeEditor: false,
    enableTerminal: false,
    enableWhiteboard: false,
    enableQuestionBoard: false,
    enableVideo: false,
    nextVideoRequested: false,
    agentIsListening: false,
    agentIsSpeaking: true,
};

const AppLayoutSlice = createSlice({
    name: 'appLayout',
    initialState,
    reducers: {
        setAgentActivity: (
            state,
            action: PayloadAction<{ is_listening: boolean; is_speaking: boolean }>,
        ) => {
            state.agentIsListening = action.payload.is_listening;
            state.agentIsSpeaking = action.payload.is_speaking;
        },
        setAgentListening: (state, action: PayloadAction<boolean>) => {
            state.agentIsListening = action.payload;
        },
        setAgentSpeaking: (state, action: PayloadAction<boolean>) => {
            state.agentIsSpeaking = action.payload;
        },
        setEnableVideo: (state, action: PayloadAction<boolean>) => {
            state.enableVideo = action.payload;
        },
        setNextVideoRequested: (state, action: PayloadAction<boolean>) => {
            state.nextVideoRequested = action.payload;
        },
        setQuestionBoard: (state, action: PayloadAction<boolean>) => {
            state.questionBoard = action.payload;
        },
        setCodeEditorBoard: (state, action: PayloadAction<boolean>) => {
            state.codeEditorBoard = action.payload;
            if (state.codeEditorBoard) {
                state.whiteboard = false; // Toggle whiteboard when code editor is set
            }
        },

        setTerminalBoard: (state, action: PayloadAction<boolean>) => {
            state.terminalBoard = action.payload;
        },
        setWhiteboardBoard: (state, action: PayloadAction<boolean>) => {
            state.whiteboard = action.payload;
            if (state.whiteboard) {
                state.codeEditorBoard = false; // Toggle code editor when whiteboard is set
            }
        },
        toggleQuestionBoard: (state) => {
            state.questionBoard = !state.questionBoard;
            if (state.questionBoard) {
                state.helpBoard = false;
            }
        },
        toggleCodeEditorBoard: (state) => {
            state.codeEditorBoard = !state.codeEditorBoard;
            if (state.codeEditorBoard) {
                state.whiteboard = false; // Toggle whiteboard when code editor is toggled
            }
        },
        toggleTerminalBoard: (state) => {
            state.terminalBoard = !state.terminalBoard;
        },
        toggleWhiteboardBoard: (state) => {
            state.whiteboard = !state.whiteboard;
            if (state.whiteboard) {
                state.codeEditorBoard = false; // Toggle code editor when whiteboard is toggled
            }
        },
        toggleHelpBoard(state) {
            state.helpBoard = !state.helpBoard;
            if (state.helpBoard) {
                state.questionBoard = false;
            }
        },

        setEnableCodeEditor: (state, action: PayloadAction<boolean>) => {
            state.enableCodeEditor = action.payload;
        },
        setEnableTerminal: (state, action: PayloadAction<boolean>) => {
            state.enableTerminal = action.payload;
        },
        setEnableWhiteboard: (state, action: PayloadAction<boolean>) => {
            state.enableWhiteboard = action.payload;
        },
        setEnableQuestionBoard: (state, action: PayloadAction<boolean>) => {
            state.enableQuestionBoard = action.payload;
        },

        resetState: (state) => {
            state.questionBoard = false;
            state.codeEditorBoard = false;
            state.terminalBoard = false;
            state.whiteboard = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(HYDRATE, (state, action) => {
            if ('payload' in action && !action.payload) {
                const hydratedState = action.payload as any;
                return {
                    ...state,
                    ...(hydratedState.appLayout || {}),
                };
            }
        });
    },
});
export const {
    setQuestionBoard,
    setCodeEditorBoard,
    setTerminalBoard,
    setWhiteboardBoard,
    toggleQuestionBoard,
    toggleCodeEditorBoard,
    toggleTerminalBoard,
    toggleWhiteboardBoard,
    setEnableCodeEditor,
    setEnableTerminal,
    setEnableWhiteboard,
    setEnableQuestionBoard,
    resetState,
    setEnableVideo,
    setNextVideoRequested,
    setAgentActivity,
    setAgentListening,
    setAgentSpeaking,
    toggleHelpBoard
} = AppLayoutSlice.actions;
export default AppLayoutSlice.reducer;
